/**
 * The app shell, laid out like Unreal Engine's editor: the menu bar on top, the toolbar under
 * it, the workspace (the windows) with the presets drawer sliding up over its bottom, the status
 * bar, toasts. Everything else lives in windows, menus and toolbar buttons the services give.
 */
import { Show, createEffect, onCleanup, onMount, type JSX } from "solid-js";
import { useApp } from "./context";
import { ContextMenuView } from "./ContextMenu";
import { installContextMenu } from "./contextMenus";
import { MenuBar } from "./MenuBar";
import { StatusBar } from "./StatusBar";
import { EnvironmentBar } from "./EnvironmentBar";
import { WorkspaceBar } from "./WorkspaceBar";
import { Toolbar } from "./Toolbar";
import { PresetDrawer } from "./PresetDrawer";
import { CommandPalette } from "./CommandPalette";
import { DEFAULT_KEYBINDS, installShortcuts } from "./keys";
import { Workspace } from "../wm/Workspace";
import { CategoryOrder, GraphKinds, Toasts } from "../shared/ui";

/** The whole page. */
export function App(): JSX.Element {
  const app = useApp();
  const { commands, config, client, spaces, accounts, wm, graph, kinds } = app;
  onMount(() =>
    onCleanup(
      installShortcuts(
        commands,
        () => config.config()?.keybinds ?? DEFAULT_KEYBINDS,
      ),
    ),
  );
  // Turned away from a space: once signed in to its server, try again (a new page connects).
  const signedInToSpace = () =>
    !!spaces.current &&
    accounts.sessions().some((s) => s.server === spaces.current?.server);
  const signedInAtStart = signedInToSpace();
  createEffect(() => {
    if (client.refused() && signedInToSpace() && !signedInAtStart)
      location.reload();
  });
  /** The app pages' sign-in, coming back here afterwards (`?next=`; the page's other parameters stay). */
  const signInAddress = () => {
    const params = new URLSearchParams(location.search);
    params.set("next", location.pathname);
    return `/?${params}`;
  };
  // The developer tools stay reachable while developing (`npm run dev`), not in builds.
  onMount(() => onCleanup(installContextMenu(app, !import.meta.env.DEV)));
  return (
    <CategoryOrder.Provider value={graph.categories}>
      <GraphKinds.Provider value={kinds.available}>
        <div class="shell">
          <MenuBar />
          <div class="toolrow">
            <EnvironmentBar />
            <WorkspaceBar />
            <Toolbar />
          </div>
          {/* Turned away from a space (signed out, or not in its team): say why, plainly. */}
          <Show when={client.refused()}>
            {(why) => (
              <div class="refused" id="refused" role="alert">
                <h2>You cannot open this space</h2>
                <p>{why()}</p>
                <p class="muted">
                  Sign in on that server as a member of the space's team, or ask
                  one of its owners for an invite link.
                </p>
                <span class="btnrow">
                  {/* A page of the server signs in on its app pages and comes back; elsewhere, in the Spaces window. */}
                  <Show
                    when={spaces.served && !spaces.remote}
                    fallback={
                      <button
                        type="button"
                        class="btn primary"
                        id="refused-signin"
                        onClick={() => wm.open("home")}
                      >
                        Sign in
                      </button>
                    }
                  >
                    <a
                      class="btn primary"
                      id="refused-signin"
                      href={signInAddress()}
                    >
                      Sign in
                    </a>
                  </Show>
                  <Show when={spaces.current}>
                    <button
                      type="button"
                      class="btn"
                      id="refused-leave"
                      onClick={() => spaces.leave()}
                    >
                      {spaces.remote
                        ? "Back to this computer"
                        : "Back to your spaces"}
                    </button>
                  </Show>
                </span>
              </div>
            )}
          </Show>
          <div class="workarea">
            <Workspace />
            <PresetDrawer />
          </div>
          <StatusBar />
          <CommandPalette />
          <ContextMenuView />
          <Toasts />
        </div>
      </GraphKinds.Provider>
    </CategoryOrder.Provider>
  );
}
